import { z } from 'zod';

// RetRef validation (transaction reference number)
const RetRefSchema = z.string()
  .min(1, { message: "RetRef is required" })
  .max(32, { message: "RetRef cannot exceed 32 characters" })
  .regex(/^[A-Za-z0-9]+$/, { message: "RetRef must be alphanumeric" });

// Merchant ID validation
const MerchantIdSchema = z.string()
  .min(1, { message: "Merchant ID must not be empty if provided" })
  .max(16, { message: "Merchant ID cannot exceed 16 characters" })
  .regex(/^\d+$/, { message: "Merchant ID must contain only digits" })
  .optional();

// Main inquire request schema (path parameters only)
const InquireRequestSchema = z.object({
  // Required path parameters
  retref: RetRefSchema,
  merchid: MerchantIdSchema
}).strict();

// Strict schema requiring all fields
const StrictInquireRequestSchema = InquireRequestSchema.safeExtend({
  retref: RetRefSchema.refine((val) => val.length >= 8, {
    message: "RetRef should be at least 8 characters for better validation"
  }),
  merchid: MerchantIdSchema.refine((val) => !val || val.length >= 10, {
    message: "Merchant ID should be at least 10 characters"
  })
});

// Path parameters schema (alias for clarity)
const InquirePathParamsSchema = InquireRequestSchema;

// Query string schema (for URL parsing)
const InquireRequestQuerySchema = z.object({
  retref: z.string()
    .min(1, { message: "RetRef is required" })
    .max(32, { message: "RetRef cannot exceed 32 characters" }),
  merchid: z.string()
    .min(1, { message: "Merchant ID must not be empty if provided" })
    .max(16, { message: "Merchant ID cannot exceed 16 characters" })
    .optional()
})
.transform((data) => ({
  retref: data.retref.trim(),
  ...(data.merchid && { merchid: data.merchid.trim() })
}));

// Path validation schema (for route parameters)
const InquirePathSchema = z.object({
  retref: z.string()
    .min(1, { message: "RetRef path parameter is required" })
    .max(32, { message: "RetRef path parameter cannot exceed 32 characters" })
    .regex(/^[A-Za-z0-9]+$/, { message: "RetRef path parameter must be alphanumeric" }),
  merchid: z.string()
    .min(1, { message: "Merchant ID path parameter is required" })
    .max(16, { message: "Merchant ID path parameter cannot exceed 16 characters" })
    .regex(/^\d+$/, { message: "Merchant ID path parameter must contain only digits" })
});

// Validation helpers
const validateRetRef = (retref: string): boolean => {
  return retref.length > 0 && retref.length <= 32 && /^[A-Za-z0-9]+$/.test(retref);
};

const validateMerchantId = (merchid: string): boolean => {
  return merchid.length > 0 && merchid.length <= 16 && /^\d+$/.test(merchid);
};

const validateInquireRequest = (data: any): boolean => {
  return validateRetRef(data.retref) && validateMerchantId(data.merchid);
};

// Type exports
export type InquireRequest = z.infer<typeof InquireRequestSchema>;
export type StrictInquireRequest = z.infer<typeof StrictInquireRequestSchema>;
export type InquirePathParams = z.infer<typeof InquirePathParamsSchema>;
export type InquireRequestQuery = z.infer<typeof InquireRequestQuerySchema>;
export type InquirePath = z.infer<typeof InquirePathSchema>;

// Schema exports
export {
  InquireRequestSchema,
  StrictInquireRequestSchema,
  InquirePathParamsSchema,
  InquireRequestQuerySchema,
  InquirePathSchema,
  RetRefSchema,
  MerchantIdSchema,
  validateRetRef,
  validateMerchantId,
  validateInquireRequest
};

// Default export
export default InquireRequestSchema; 